import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import InvoiceTile from "@/components/app/InvoiceTile";
import QuickInvoiceCapture from "@/components/app/QuickInvoiceCapture";
import OnboardingWizard from "@/components/app/OnboardingWizard";
import { toast } from "sonner";
import { Flame, Bell, Play, Thermometer, SprayCan, PackageCheck, ClipboardCheck, ChefHat, Receipt, CalendarClock, Check, ArrowRight, GraduationCap, Users, Clock, Sparkles, Trophy, Award, Camera } from "lucide-react";

const C = { night: "#101828", saffron: "#FBF3E4", basil: "#E3EFD9", tandoor: "#F6DDD2", ink: "#1B2D5C", green: "#5BC044", amber: "#D9931C", red: "#C2410C" };

// Kleurschaal: rood <25%, oranje <50%, geel t/m 80%, daarna steeds groener tot 100%
function scoreRGB(pct) {
  if (pct < 25) return [224, 83, 58];
  if (pct < 50) return [240, 135, 58];
  if (pct <= 80) return [245, 194, 107];
  const t = Math.min(1, (pct - 80) / 20);
  const a = [245, 194, 107], b = [91, 192, 68];
  return a.map((v, i) => Math.round(v + (b[i] - v) * t));
}
export function scoreColor(pct, alpha = 1) { const [r, g, b] = scoreRGB(pct); return alpha >= 1 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${alpha})`; }

const TASK_ICON = { temperatuur: Thermometer, schoonmaak: SprayCan, ingangscontrole: PackageCheck, hygienecheck: ClipboardCheck, bereiding: ChefHat };

export default function DashboardToday() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [restoId, setRestoId] = useState("");
  const [leaving, setLeaving] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const prevKeysRef = useRef(null);
  const [confetti, setConfetti] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const prevOpenRef = useRef(null);


  const load = useCallback(() => {
    const q = restoId ? `?restaurant_id=${restoId}` : "";
    return api.get(`/dashboard/today${q}`).then((r) => {
      const d = r.data;
      const openKeys = new Set(d.tasks.map((t) => t.key));
      const prev = prevKeysRef.current ?? JSON.parse(sessionStorage.getItem(`stockly_tasks_${restoId}`) || "null");
      if (prev) {
        const done = prev.filter((t) => !openKeys.has(t.key));
        if (done.length) {
          setLeaving(done);
          setTimeout(() => setLeaving([]), 1100);
        }
      }
      prevKeysRef.current = d.tasks.map((t) => ({ key: t.key, title: t.title, restaurant_name: t.restaurant_name, kind: t.kind, type: t.type }));
      sessionStorage.setItem(`stockly_tasks_${restoId}`, JSON.stringify(prevKeysRef.current));
      if (prevOpenRef.current !== null && prevOpenRef.current > 0 && d.tasks_open === 0) {
        setConfetti(true);
        setTimeout(() => setConfetti(false), 2600);
      }
      prevOpenRef.current = d.tasks_open;
      setData(d);
      setRefreshKey((k) => k + 1);
    }).catch(() => {});
  }, [restoId]);

  useEffect(() => { prevKeysRef.current = null; prevOpenRef.current = null; load(); }, [load]);
  useEffect(() => { const onFocus = () => load(); window.addEventListener("focus", onFocus); return () => window.removeEventListener("focus", onFocus); }, [load]);

  const isAdmin = ["admin", "organization_admin", "manager"].includes(user?.role);
  useEffect(() => {
    if (!data || !["admin", "organization_admin"].includes(user?.role)) return;
    const dismissed = localStorage.getItem("stockly_onboarding_dismissed") === "1" || localStorage.getItem("stockly_onboarding_done") === "1";
    if (!dismissed && data.restaurants.length === 0) setShowOnboarding(true);
  }, [data, user]);
  const canRemind = ["admin", "organization_admin", "manager"].includes(user?.role);

  if (!data) return <div className="p-10 text-zinc-500" data-testid="preview-loading">Laden…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto pb-28 lg:pb-8" data-testid="dashboard-page">
      {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} />}
      {confetti && <Confetti />}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="overline" data-testid="today-overline">Vandaag</div>
        <div className="flex items-center gap-2">
          {["admin", "organization_admin"].includes(user?.role) && (
            <button onClick={() => { localStorage.removeItem("stockly_onboarding_dismissed"); localStorage.removeItem("stockly_onboarding_done"); setShowOnboarding(true); }}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-zinc-200 bg-white hover:border-[#101828] text-xs font-semibold text-zinc-700" data-testid="dashboard-open-onboarding">
              <Sparkles size={13} /> Setup-gids
            </button>
          )}
          <Link to="/dashboard-klassiek" className="text-xs text-zinc-500 hover:text-zinc-900 underline-offset-2 hover:underline" data-testid="today-classic-link">Klassieke weergave</Link>
        </div>
      </div>

      {data.restaurants.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1" data-testid="preview-resto-pills">
          <Pill active={!restoId} onClick={() => setRestoId("")} testid="preview-pill-all">Alle restaurants</Pill>
          {data.restaurants.map((r) => <Pill key={r.id} active={restoId === r.id} onClick={() => setRestoId(r.id)} testid={`preview-pill-${r.id}`}>{r.name.trim()}</Pill>)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5">
        <div className="lg:col-span-8 reveal" style={{ "--d": "0ms" }}>
          <HeroTile data={data} canRemind={canRemind} restoId={restoId} onReload={load} />
        </div>
        <div className="lg:col-span-4 lg:row-span-2 reveal" style={{ "--d": "80ms" }}>
          <TaskTile data={data} leaving={leaving} isAdmin={isAdmin} onReload={load} />
        </div>
        <div className="lg:col-span-4 reveal" style={{ "--d": "160ms" }}>
          <InvoiceTile restaurantId={restoId} refreshKey={refreshKey} variant="bento" />
        </div>
        <div className="lg:col-span-4 reveal" style={{ "--d": "240ms" }}>
          <PerRestaurantTile items={data.per_restaurant} />
        </div>
        {data.ranking && (
          <div className="lg:col-span-4 reveal" style={{ "--d": "300ms" }}>
            <RankingTile ranking={data.ranking} />
          </div>
        )}
        <div className="lg:col-span-4 reveal" style={{ "--d": "320ms" }}>
          <NextInspectionTile item={data.next_inspection} />
        </div>
        {data.training && (
          <div className="lg:col-span-4 reveal" style={{ "--d": "400ms" }}>
            <TrainingTile training={data.training} />
          </div>
        )}
        <div className="lg:col-span-4 reveal" style={{ "--d": "480ms" }}>
          <ShiftTile occupancy={data.occupancy} isAdmin={isAdmin} />
        </div>
      </div>

      <style>{`
        .reveal { animation: reveal-up .55s cubic-bezier(.2,.7,.2,1) both; animation-delay: var(--d, 0ms); }
        @keyframes reveal-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        .task-leave { animation: task-leave .95s cubic-bezier(.2,.7,.2,1) forwards; }
        @keyframes task-leave { 0% { background: #DCFCE7; transform: none; opacity: 1; } 55% { background: #BBF7D0; transform: translateX(6px); opacity: 1; } 100% { transform: translateX(110%); opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; margin: 0; } }
        .ring-fg { transition: stroke-dashoffset 1.1s cubic-bezier(.2,.7,.2,1); }
        @media (prefers-reduced-motion: reduce) { .reveal, .task-leave { animation: none !important; } .ring-fg { transition: none; } }
      `}</style>
    </div>
  );
}

function Pill({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`shrink-0 h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${active ? "bg-[#101828] text-white border-[#101828]" : "bg-white text-zinc-700 border-zinc-300 hover:border-[#101828]"}`}>
      {children}
    </button>
  );
}

function Ring({ value, size = 168, stroke = 12, color }) {
  const [v, setV] = useState(() => Number(sessionStorage.getItem("stockly_prev_score") || 0));
  useEffect(() => { const t = setTimeout(() => { setV(value); sessionStorage.setItem("stockly_prev_score", String(value)); }, 60); return () => clearTimeout(t); }, [value]);
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="preview-ring">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.12)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round" className="ring-fg"
          strokeDasharray={circ} strokeDashoffset={circ - (circ * Math.min(100, v)) / 100} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-white font-extrabold tabular-nums" style={{ fontSize: size * 0.25 }} data-testid="preview-score">{value}%</div>
    </div>
  );
}

function hygieneHref(data, type) {
  const open = data.tasks.find((t) => t.kind === "hygiene" && (!type || t.type === type));
  if (open) return open.href;
  const rid = data.selected_restaurant_id || data.restaurants[0]?.id;
  const t = type || data.hygiene_by_type[0]?.type || "temperatuur";
  return rid ? `/documenten?open=${t}&restaurant_id=${rid}` : "/documenten";
}

function HeroTile({ data, canRemind, restoId, onReload }) {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const dt = new Date(data.date);
  const dateStr = dt.toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" });
  const color = scoreColor(data.score);
  const open = data.tasks_open;
  const nudge = open === 0 ? "Klaar voor vandaag" : open === 1 ? "Nog 1 taak, dan ben je klaar" : `Nog ${open} taken en je staat op 100%`;
  const roundHref = hygieneHref(data, null);
  const remind = async () => {
    setSending(true);
    try {
      const r = await api.post(`/dashboard/remind_chefs${restoId ? `?restaurant_id=${restoId}` : ""}`);
      toast.success(r.data.sent ? `Herinnering verstuurd naar ${r.data.sent} chef${r.data.sent === 1 ? "" : "s"}` : "Niemand te herinneren (alles klaar of al gemaild vandaag)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Versturen mislukt"); }
    finally { setSending(false); }
  };
  return (
    <section className="rounded-3xl p-6 sm:p-8 text-white h-full" style={{ background: C.night }} data-testid="preview-hero">
      <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50">{dateStr} · week {data.week_nr}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2" data-testid="preview-greeting">{data.greeting}, {data.first_name}.</h1>
          <div className="mt-2 text-white/70 text-sm sm:text-base inline-flex items-center gap-2" data-testid="preview-nudge">
            {open === 0 ? <Check size={16} className="text-[#5BC044]" /> : null}{nudge}
            {data.best_streak > 0 && <span className="inline-flex items-center gap-1 text-[#F5C26B] text-xs font-bold ml-1"><Flame size={13} /> {data.best_streak} dagen op rij</span>}
            {data.week_streak > 0 && <span className="inline-flex items-center gap-1 text-white text-xs font-bold ml-1 bg-white/10 rounded-full px-2.5 h-6" data-testid="preview-week-streak"><Flame size={13} className="text-[#F59E7B]" /> {data.week_streak} {data.week_streak === 1 ? "week" : "weken"} op rij inspectie-klaar</span>}
          </div>
          <div className="mt-6">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50 mb-2">Hygiëne — wat aandacht nodig heeft</div>
            <QuickInvoiceCapture onSaved={onReload} defaultRestaurantId={restoId || undefined}>
              {(openCamera) => (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="preview-hygiene-parts">
                  {data.hygiene_by_type.map((h) => {
                    const Icon = TASK_ICON[h.type] || ClipboardCheck;
                    const done = h.open === 0;
                    const isIntake = h.type === "ingangscontrole";
                    const href = hygieneHref(data, h.type);
                    return (
                      <button key={h.type} type="button" onClick={() => (isIntake ? openCamera() : navigate(href))}
                        title={isIntake ? "Fotografeer de factuur voor de ingangscontrole" : done ? "Open de lijst" : "Open de lijst die nog bijgewerkt moet worden"}
                        className="relative text-left rounded-2xl p-3 transition-colors hover:brightness-110"
                        style={{ background: scoreColor(h.pct, 0.16), boxShadow: `inset 0 0 0 1px ${scoreColor(h.pct, 0.35)}` }}
                        data-testid={`preview-hyg-${h.type}`}>
                        {isIntake && <span className="absolute bottom-2.5 right-2.5 w-6 h-6 rounded-full bg-white/15 text-white flex items-center justify-center" data-testid="preview-hyg-camera-icon"><Camera size={12} /></span>}
                        <div className={`inline-flex items-center gap-1.5 text-[11px] font-semibold `} style={{ color: scoreColor(h.pct) }}><Icon size={12} /> {h.label}</div>
                        <div className="text-lg font-extrabold font-mono mt-1" data-testid={`preview-hyg-pct-${h.type}`}>{h.pct}%</div>
                        <div className="text-[11px]" style={{ color: done ? scoreColor(h.pct) : "rgba(255,255,255,0.6)" }}>{done ? "Voltooid" : `${h.open} open · ${h.due}`}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </QuickInvoiceCapture>
            <div className="mt-3 text-xs text-white/45 flex flex-wrap gap-x-4 gap-y-1" data-testid="preview-secondary-scores">
              <span data-testid="preview-sub-documents">Vergunningen geldig {data.breakdown.documents.ok}/{data.breakdown.documents.total}</span>
              <span data-testid="preview-sub-inspections">Keuringen op tijd {data.breakdown.inspections.ok}/{data.breakdown.inspections.total}</span>
            </div>
          </div>
          <p className="mt-5 text-sm sm:text-base text-white/80 max-w-md" data-testid="preview-advice">{data.advice}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            <button onClick={() => navigate(roundHref)} className="h-11 px-5 rounded-full bg-[#5BC044] hover:bg-[#4FAA3A] text-white font-bold text-sm inline-flex items-center gap-2 transition-colors" data-testid="preview-start-round">
              <Play size={15} /> Start hygiëneronde
            </button>
            {canRemind && (
              <button onClick={remind} disabled={sending} className="h-11 px-5 rounded-full border border-white/25 hover:bg-white/10 text-white font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60 transition-colors" data-testid="preview-remind-chefs">
                <Bell size={15} /> {sending ? "Versturen…" : "Herinner chefs"}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center gap-2 self-center">
          <Ring value={data.score} color={color} />
          <div className="text-xs font-semibold uppercase tracking-wider text-white/50">inspectie-klaar</div>
        </div>
      </div>
    </section>
  );
}


function TaskTile({ data, leaving, isAdmin, onReload }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const tasks = useMemo(() => data.tasks.slice(0, 12), [data.tasks]);
  const complete = async (t) => {
    setBusy(t.key);
    try {
      if (t.kind === "inspection") await api.put(`/inspections/${t.id}`, { mark_done: true });
      else if (t.kind === "invoice") await api.patch(`/invoice_checks/${t.id}/status`, { status: "approved" });
      await onReload();
    } catch { toast.error("Afvinken mislukt"); }
    finally { setBusy(""); }
  };
  return (
    <section className="rounded-3xl p-6 h-full" style={{ background: C.saffron }} data-testid="preview-tasks">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">Nu doen</div>
          <h2 className="text-xl font-bold tracking-tight text-[#101828]">{data.tasks_open === 0 ? "Alles afgerond" : `${data.tasks_open} ${data.tasks_open === 1 ? "taak" : "taken"} open`}</h2>
        </div>
        <span className="w-10 h-10 rounded-2xl bg-white/70 flex items-center justify-center text-[#D9931C]"><ClipboardCheck size={20} /></span>
      </div>
      <QuickInvoiceCapture onSaved={onReload}>
        {(openCamera) => (
      <ul className="space-y-2" data-testid="preview-task-list">
        {leaving.map((t) => (
          <li key={`leave-${t.key}`} className="task-leave rounded-2xl px-3 py-3 flex items-center gap-3 overflow-hidden" data-testid={`preview-task-leaving-${t.key}`}>
            <span className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0"><Check size={16} /></span>
            <div className="min-w-0 flex-1"><div className="font-semibold text-sm line-through text-emerald-800 truncate">{t.title}</div><div className="text-xs text-emerald-700 truncate">{t.restaurant_name} · afgerond</div></div>
          </li>
        ))}
        {tasks.length === 0 && leaving.length === 0 && (
          <li className="rounded-2xl bg-white/70 px-4 py-6 text-center text-sm text-zinc-600" data-testid="preview-tasks-empty">
            <Check size={22} className="mx-auto mb-2 text-emerald-600" /> Klaar voor vandaag — niets open.
          </li>
        )}
        {tasks.map((t) => {
          const Icon = t.kind === "invoice" ? Receipt : t.kind === "inspection" ? CalendarClock : (TASK_ICON[t.type] || ClipboardCheck);
          const late = t.kind === "inspection" || t.risk === "hoog";
          const badge = late ? "bg-[#F6DDD2] text-[#9A3412]" : t.due === "vandaag" ? "bg-[#FDE9C2] text-[#8A5A0B]" : "bg-white text-zinc-600";
          const canTick = (t.kind === "inspection" && isAdmin) || t.kind === "invoice";
          return (
            <li key={t.key} className="rounded-2xl bg-white/80 px-3 py-2.5 flex items-center gap-3 transition-colors hover:bg-white" data-testid={`preview-task-${t.key}`}>
              <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${late ? "bg-[#F6DDD2] text-[#C2410C]" : "bg-[#E3EFD9] text-[#2F6B1F]"}`}><Icon size={17} /></span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm text-[#101828] truncate">{t.title}</div>
                <div className="text-xs text-zinc-500 truncate">{t.restaurant_name || "Organisatie"}{t.minutes ? ` · ${t.minutes} min` : ""}</div>
              </div>
              <span className={`text-[10px] font-bold px-2 h-5 inline-flex items-center rounded-full shrink-0 ${badge}`} data-testid={`preview-task-due-${t.key}`}>{t.due}</span>
              {canTick ? (
                <button onClick={() => complete(t)} disabled={busy === t.key} className="w-9 h-9 rounded-full bg-[#101828] text-white flex items-center justify-center shrink-0 hover:bg-[#1B2D5C] active:scale-95 transition-transform disabled:opacity-50" aria-label="Afvinken" data-testid={`preview-task-done-${t.key}`}>
                  <Check size={16} />
                </button>
              ) : (
                <button onClick={() => (t.type === "ingangscontrole" ? openCamera() : navigate(t.href))} className="w-9 h-9 rounded-full bg-[#101828] text-white flex items-center justify-center shrink-0 hover:bg-[#1B2D5C] active:scale-95 transition-transform" aria-label={t.type === "ingangscontrole" ? "Factuur fotograferen" : "Openen"} data-testid={`preview-task-open-${t.key}`}>
                  {t.type === "ingangscontrole" ? <Camera size={16} /> : <ArrowRight size={16} />}
                </button>
              )}
            </li>
          );
        })}
      </ul>
        )}
      </QuickInvoiceCapture>
      {data.tasks.length > 12 && <div className="text-xs text-zinc-500 mt-3">+ {data.tasks.length - 12} meer — filter op restaurant om te focussen.</div>}
    </section>
  );
}

function PerRestaurantTile({ items }) {
  return (
    <section className="rounded-3xl p-6 h-full" style={{ background: C.basil }} data-testid="preview-per-restaurant">
      <div className="overline">Per restaurant</div>
      <h2 className="text-xl font-bold tracking-tight text-[#101828] mb-4">Hygiëne deze week</h2>
      <div className="space-y-3">
        {items.map((r) => {
          const pct = Math.round((r.hygiene_done / r.hygiene_total) * 100);
          return (
            <div key={r.restaurant_id} data-testid={`preview-resto-${r.restaurant_id}`}>
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-semibold text-[#101828] inline-flex items-center gap-1.5">{r.name.trim()}
                  {r.streak > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#B45309] bg-[#FDE9C2] px-1.5 h-4 rounded-full" data-testid={`preview-streak-${r.restaurant_id}`}><Flame size={10} /> {r.streak}</span>}
                </span>
                <span className="font-mono text-zinc-600" data-testid={`preview-resto-pct-${r.restaurant_id}`}>{pct}%</span>
              </div>
              <div className="h-2 bg-white/70 rounded-full overflow-hidden"><div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: scoreColor(pct) }} /></div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function NextInspectionTile({ item }) {
  return (
    <section className="rounded-3xl p-6 h-full" style={{ background: C.tandoor }} data-testid="preview-next-inspection">
      <div className="overline">Volgende keuring</div>
      {!item ? <div className="text-sm text-zinc-600 mt-2">Geen keuringen gepland.</div> : (
        <>
          <div className="flex items-end gap-2 mt-1">
            <div className="text-5xl font-extrabold text-[#101828] font-mono leading-none" data-testid="preview-inspection-days">{item.days_left}</div>
            <div className="text-sm text-zinc-600 pb-1">dagen</div>
          </div>
          <div className="mt-2 font-bold text-[#101828]">{item.name}</div>
          <div className="text-sm text-zinc-600">{item.restaurant_name.trim()} · {item.next_due}</div>
          {item.combine_with?.length > 0 && (
            <div className="mt-3 rounded-2xl bg-white/70 p-3 text-xs text-[#9A3412]" data-testid="preview-inspection-combine">
              Tip: combineer met {item.combine_with.map((s) => s.trim()).join(", ")} tot één bezoek.
            </div>
          )}
        </>
      )}
    </section>
  );
}

function TrainingTile({ training }) {
  return (
    <section className="rounded-3xl p-6 h-full bg-white border border-zinc-200" data-testid="preview-training">
      <div className="flex items-center justify-between mb-3">
        <div><div className="overline">HACCP-training</div><h2 className="text-xl font-bold tracking-tight text-[#101828]">{training.certified}/{training.total} gecertificeerd</h2></div>
        <span className="w-10 h-10 rounded-2xl bg-[#E3EFD9] text-[#2F6B1F] flex items-center justify-center"><GraduationCap size={20} /></span>
      </div>
      <div className="space-y-2">
        {training.items.map((it) => (
          <div key={it.user_id} className="flex items-center gap-3 text-sm" data-testid={`preview-training-${it.user_id}`}>
            <div className="flex-1 min-w-0 truncate font-medium">{it.name || it.email}</div>
            <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((L) => <span key={L} className={`w-5 h-1.5 rounded-full ${it.levels_passed.includes(L) ? "bg-[#5BC044]" : "bg-zinc-200"}`} />)}</div>
          </div>
        ))}
        {training.items.length === 0 && <div className="text-sm text-zinc-500">Nog geen teamleden.</div>}
      </div>
    </section>
  );
}

function ShiftTile({ occupancy, isAdmin }) {
  return (
    <section className="rounded-3xl p-6 h-full text-white" style={{ background: C.night }} data-testid="preview-shift">
      <div className="flex items-center justify-between">
        <div><div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/50">Nu aanwezig</div>
          <h2 className="text-xl font-bold tracking-tight mt-1">{occupancy.count === 0 ? "Niemand ingeklokt" : `${occupancy.count} ingeklokt`}</h2></div>
        <span className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center"><Users size={20} /></span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {occupancy.by_function.map((f) => <span key={f.function} className="text-xs bg-white/10 rounded-full px-2.5 h-6 inline-flex items-center">{f.label}: {f.count}</span>)}
      </div>
      <Link to={isAdmin ? "/shiftregistratie" : "/profiel"} className="mt-5 h-11 px-5 rounded-full bg-white text-[#101828] font-bold text-sm inline-flex items-center gap-2 hover:bg-zinc-100 transition-colors" data-testid="preview-clock-in">
        <Clock size={15} /> Inklokken
      </Link>
    </section>
  );
}

const CONFETTI_COLORS = ["#5BC044", "#F5C26B", "#F59E7B", "#1B2D5C", "#8FE07A", "#FBF3E4"];
function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 70 }, (_, i) => ({
    id: i, left: Math.random() * 100, delay: Math.random() * 0.5, dx: (Math.random() - 0.5) * 240, rot: 360 + Math.random() * 900,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length], scale: 0.7 + Math.random() * 0.8,
  })), []);
  return (
    <div aria-hidden data-testid="confetti">
      {pieces.map((p) => (
        <span key={p.id} className="confetti-piece" style={{ left: `${p.left}vw`, background: p.color, animationDelay: `${p.delay}s`, transform: `scale(${p.scale})`, "--dx": `${p.dx}px`, "--rot": `${p.rot}deg` }} />
      ))}
    </div>
  );
}

function RankingTile({ ranking }) {
  const medal = ["bg-[#F5C26B] text-[#7C4A03]", "bg-zinc-200 text-zinc-700", "bg-[#F6DDD2] text-[#9A3412]"];
  return (
    <section className="rounded-3xl p-6 h-full bg-white border border-zinc-200" data-testid="preview-ranking">
      <div className="flex items-center justify-between mb-3">
        <div><div className="overline">Team-ranglijst</div><h2 className="text-xl font-bold tracking-tight text-[#101828]">Deze week</h2></div>
        <span className="w-10 h-10 rounded-2xl bg-[#FBF3E4] text-[#D9931C] flex items-center justify-center"><Trophy size={20} /></span>
      </div>
      <ol className="space-y-2">
        {ranking.map((r) => (
          <li key={r.restaurant_id} className={`flex items-center gap-3 rounded-2xl px-3 py-2 ${r.rank === 1 ? "bg-[#FBF3E4]" : "bg-zinc-50"}`} data-testid={`preview-rank-${r.restaurant_id}`}>
            <span className={`w-7 h-7 rounded-full text-xs font-extrabold flex items-center justify-center shrink-0 ${medal[r.rank - 1] || "bg-zinc-100 text-zinc-500"}`}>{r.rank}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#101828] truncate">{r.name.trim()}</div>
              <div className="text-xs text-zinc-500">{r.pct}% hygiëne{r.streak > 0 ? ` · ${r.streak} dagen op rij` : ""}</div>
            </div>
            {r.top && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#7C4A03] bg-[#F5C26B] px-2 h-6 rounded-full shrink-0" data-testid="preview-rank-badge"><Award size={11} /> Meest consistente team</span>}
          </li>
        ))}
      </ol>
    </section>
  );
}
